import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import api, { formatApiError } from "../lib/api";
import { useI18n } from "../contexts/I18nContext";
import MobileFrame from "../components/MobileFrame";
import { Save, X, Wand2, ExternalLink, Copy, Trash2, Check, Upload, ImageIcon } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const DEFAULTS = {
  name: "",
  url: "",
  short_name: "",
  description: "",
  icon_url: "",
  theme_color: "#0A0A0A",
  background_color: "#FAFAF7",
  display: "standalone",
  orientation: "any",
};

export default function Builder() {
  const { id } = useParams();
  const isEdit = !!id;
  const navigate = useNavigate();
  const { t } = useI18n();

  const [form, setForm] = useState(DEFAULTS);
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);
  const [fetching, setFetching] = useState(false);
  const [savedApp, setSavedApp] = useState(null);
  const [copied, setCopied] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);

  useEffect(() => {
    if (!isEdit) return;
    api.get(`/apps/${id}`).then((r) => {
      setForm({ ...DEFAULTS, ...r.data });
      setSavedApp(r.data);
    }).catch(() => navigate("/dashboard"));
  }, [id, isEdit, navigate]);

  const installLink = useMemo(() => {
    if (!savedApp) return "";
    return `${window.location.origin}/a/${savedApp.slug}`;
  }, [savedApp]);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const onAutoFill = async () => {
    if (!form.url) return;
    setFetching(true);
    setError("");
    try {
      const { data } = await api.post("/fetch-meta", { url: form.url });
      setForm((f) => ({
        ...f,
        url: data.url || f.url,
        name: f.name || data.title || "",
        short_name: f.short_name || (data.title || "").slice(0, 24),
        description: f.description || data.description || "",
        icon_url: f.icon_url || data.icon_url || "",
        theme_color: (f.theme_color === "#0A0A0A" && data.theme_color) ? data.theme_color : f.theme_color,
      }));
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setFetching(false);
    }
  };

  const onSave = async (e) => {
    e.preventDefault();
    setError("");
    setSaving(true);
    try {
      const payload = { ...form };
      if (isEdit) {
        const { data } = await api.put(`/apps/${id}`, payload);
        setSavedApp(data);
      } else {
        const { data } = await api.post("/apps", payload);
        setSavedApp(data);
        navigate(`/builder/${data.id}`, { replace: true });
      }
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setSaving(false);
    }
  };

  const onDelete = async () => {
    if (!savedApp) return;
    if (!window.confirm(t("builder_delete_confirm"))) return;
    await api.delete(`/apps/${savedApp.id}`);
    navigate("/dashboard");
  };

  const onCopy = async () => {
    if (!installLink) return;
    try { await navigator.clipboard.writeText(installLink); } catch {}
    setCopied(true);
    setTimeout(() => setCopied(false), 1800);
  };

  const onUploadIcon = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 2 * 1024 * 1024) {
      setError("Image too large (max 2MB)");
      return;
    }
    const fd = new FormData();
    fd.append("file", f);
    setUploading(true);
    setError("");
    try {
      const { data } = await api.post("/upload/icon", fd, { headers: { "Content-Type": "multipart/form-data" } });
      // Backend returns "/api/files/{id}" — make absolute so the manifest works for PWA installs
      const absolute = data.url.startsWith("http") ? data.url : `${BACKEND_URL}${data.url}`;
      update("icon_url", absolute);
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const previewIconUrl = form.icon_url;

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 py-12">
      <h1 className="font-display text-3xl sm:text-4xl font-bold tracking-tight mb-10" data-testid="builder-title">
        {isEdit ? t("builder_edit_title") : t("builder_new_title")}
      </h1>

      <div className="grid lg:grid-cols-12 gap-8">
        {/* Form */}
        <form onSubmit={onSave} className="lg:col-span-7 space-y-5" data-testid="builder-form">
          <div className="surface-card p-6 sm:p-7 space-y-5">
            <div>
              <label className="input-label">{t("builder_url_label")}</label>
              <div className="flex gap-2">
                <input
                  type="text"
                  value={form.url}
                  onChange={(e) => update("url", e.target.value)}
                  placeholder={t("builder_url_placeholder")}
                  required
                  data-testid="builder-url"
                  className="input flex-1"
                />
                <button
                  type="button"
                  onClick={onAutoFill}
                  disabled={!form.url || fetching}
                  data-testid="builder-fetch"
                  className="btn btn-secondary disabled:opacity-60"
                >
                  <Wand2 className="w-4 h-4" strokeWidth={2} />
                  {fetching ? t("common_loading") : t("builder_fetch")}
                </button>
              </div>
            </div>

            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <label className="input-label">{t("builder_name_label")}</label>
                <input value={form.name} onChange={(e) => update("name", e.target.value)} required maxLength={60} className="input" data-testid="builder-name" />
              </div>
              <div>
                <label className="input-label">{t("builder_short_name_label")}</label>
                <input value={form.short_name} onChange={(e) => update("short_name", e.target.value)} maxLength={24} className="input" data-testid="builder-short-name" />
              </div>
            </div>

            <div>
              <label className="input-label">{t("builder_desc_label")}</label>
              <textarea value={form.description} onChange={(e) => update("description", e.target.value)} maxLength={240} rows={2} className="input resize-none" data-testid="builder-desc" />
            </div>

            <div>
              <label className="input-label">{t("builder_icon_label")}</label>
              <div className="flex flex-col sm:flex-row gap-2">
                <input
                  value={form.icon_url}
                  onChange={(e) => update("icon_url", e.target.value)}
                  className="input flex-1 font-mono text-sm"
                  placeholder={t("builder_icon_url_placeholder")}
                  data-testid="builder-icon"
                />
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/png,image/jpeg,image/webp,image/gif,image/svg+xml"
                  onChange={onUploadIcon}
                  className="hidden"
                  data-testid="builder-icon-file"
                />
                <button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={uploading}
                  data-testid="builder-icon-upload"
                  className="btn btn-secondary disabled:opacity-60"
                >
                  <Upload className="w-4 h-4" strokeWidth={2} />
                  {uploading ? t("builder_icon_uploading") : t("builder_icon_upload")}
                </button>
              </div>
              <div className="flex items-center gap-3 mt-2">
                {previewIconUrl ? (
                  <span className="w-9 h-9 rounded-lg border border-zinc-200 overflow-hidden grid place-items-center bg-white flex-shrink-0">
                    <img src={previewIconUrl} alt="" className="w-full h-full object-cover" onError={(e)=>{e.currentTarget.style.display="none";}} />
                  </span>
                ) : (
                  <span className="w-9 h-9 rounded-lg border border-zinc-200 grid place-items-center bg-zinc-50 text-zinc-400 flex-shrink-0">
                    <ImageIcon className="w-4 h-4" strokeWidth={1.6} />
                  </span>
                )}
                <p className="text-xs text-zinc-500 leading-snug">{t("builder_icon_size_hint")}</p>
              </div>
            </div>

            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <label className="input-label">{t("builder_theme_label")}</label>
                <div className="flex items-center gap-3">
                  <input type="color" value={form.theme_color} onChange={(e) => update("theme_color", e.target.value)} className="w-12 h-11 border border-zinc-200 rounded-lg cursor-pointer bg-white p-1" data-testid="builder-theme" />
                  <input value={form.theme_color} onChange={(e) => update("theme_color", e.target.value)} className="input flex-1 font-mono text-sm" />
                </div>
              </div>
              <div>
                <label className="input-label">{t("builder_bg_label")}</label>
                <div className="flex items-center gap-3">
                  <input type="color" value={form.background_color} onChange={(e) => update("background_color", e.target.value)} className="w-12 h-11 border border-zinc-200 rounded-lg cursor-pointer bg-white p-1" data-testid="builder-bg" />
                  <input value={form.background_color} onChange={(e) => update("background_color", e.target.value)} className="input flex-1 font-mono text-sm" />
                </div>
              </div>
            </div>

            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <label className="input-label">{t("builder_display_label")}</label>
                <select value={form.display} onChange={(e) => update("display", e.target.value)} className="input" data-testid="builder-display">
                  <option value="standalone">standalone</option>
                  <option value="fullscreen">fullscreen</option>
                  <option value="minimal-ui">minimal-ui</option>
                </select>
              </div>
              <div>
                <label className="input-label">{t("builder_orientation_label")}</label>
                <select value={form.orientation} onChange={(e) => update("orientation", e.target.value)} className="input" data-testid="builder-orientation">
                  <option value="any">any</option>
                  <option value="portrait">portrait</option>
                  <option value="landscape">landscape</option>
                </select>
              </div>
            </div>

            {error && (
              <div className="rounded-xl bg-red-50 border border-red-200 text-red-700 px-3.5 py-3 text-sm" data-testid="builder-error">
                {error}
              </div>
            )}

            <div className="flex flex-wrap gap-3 pt-1">
              <button type="submit" disabled={saving} data-testid="builder-save" className="btn btn-primary disabled:opacity-60">
                <Save className="w-4 h-4" strokeWidth={2} />
                {saving ? t("common_loading") : t("builder_save")}
              </button>
              <button type="button" onClick={() => navigate("/dashboard")} data-testid="builder-cancel" className="btn btn-secondary">
                <X className="w-4 h-4" strokeWidth={2} />
                {t("builder_cancel")}
              </button>
              {isEdit && (
                <button type="button" onClick={onDelete} data-testid="builder-delete" className="btn btn-danger ms-auto">
                  <Trash2 className="w-4 h-4" strokeWidth={2} />
                  {t("builder_delete")}
                </button>
              )}
            </div>
          </div>

          {savedApp && installLink && (
            <div className="surface-card p-6" data-testid="builder-share-block" style={{ background: "linear-gradient(135deg, #FAFAF7 0%, #F4F4F5 100%)" }}>
              <p className="input-label !mb-2">{t("builder_open_install")}</p>
              <div className="flex flex-wrap gap-2 items-center">
                <code
                  className="flex-1 min-w-0 font-mono text-xs sm:text-sm bg-white border border-zinc-200 rounded-xl px-3 py-2.5 break-all"
                  data-testid="builder-install-link"
                >
                  {installLink}
                </code>
                <button type="button" onClick={onCopy} data-testid="builder-copy" className="btn btn-secondary btn-sm">
                  {copied ? <Check className="w-3.5 h-3.5" strokeWidth={2.5} /> : <Copy className="w-3.5 h-3.5" strokeWidth={2} />}
                  {copied ? t("builder_link_copied") : t("builder_copy_link")}
                </button>
                <a href={installLink} target="_blank" rel="noreferrer" data-testid="builder-open" className="btn btn-primary btn-sm">
                  <ExternalLink className="w-3.5 h-3.5" strokeWidth={2} />
                  {t("builder_open_install")}
                </a>
              </div>
            </div>
          )}
        </form>

        {/* Preview */}
        <aside className="lg:col-span-5">
          <div className="lg:sticky lg:top-24 max-w-[280px] mx-auto">
            <p className="input-label text-center mb-4">{t("builder_preview")}</p>
            <MobileFrame
              appName={form.name || "App"}
              shortName={form.short_name || form.name || "App"}
              iconUrl={form.icon_url}
              themeColor={form.theme_color}
              backgroundColor={form.background_color}
              url={form.url}
            />
          </div>
        </aside>
      </div>
    </div>
  );
}
